import { useEffect, useRef, type ReactNode } from 'react';
import { usePrefersReducedMotion } from '../use-prefers-reduced-motion';

// ~15px/s: a hint that the row continues, not something moving under you while you read.
export const DRIFT_PX_PER_MS = 0.015;
const DRIFT_RESUME_MS = 2500;

/**
 * Drifts on its own, so the row reads as continuing past the screen edge. The drift is a `transform` on an
 * inner track, not an animated `scrollLeft`: a scroll offset rounds to a whole pixel, so at 15px/s the row
 * jumped fifteen times a second (at 120Hz seven frames in eight were dead), while a transform stays sub-pixel
 * and leaves the main thread to the live counters. Scrolling stays the browser's own: a pointer press folds
 * the drift into the real scroll position, and it restarts from there. It ping-pongs so no tile is
 * duplicated, and pauses off screen.
 */
export function SwipeRow(props: { children: ReactNode; speed: number }) {
	const scroller = useRef<HTMLDivElement>(null);
	const track = useRef<HTMLDivElement>(null);
	const drift = useRef<Animation | null>(null);
	const restart = useRef<(() => void) | null>(null);
	const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const held = useRef(false);
	const onScreen = useRef(false);
	const reduce = usePrefersReducedMotion();

	/** Moves the drift's offset into `scrollLeft`, so finger and snap points act where the tiles appear. */
	const fold = () => {
		const el = scroller.current;
		const inner = track.current;
		if (!el || !inner || !drift.current) return;
		const drifted = -new DOMMatrix(getComputedStyle(inner).transform).m41;
		// Same task, so the dropped transform and the new scroll never render apart.
		drift.current.cancel();
		drift.current = null;
		el.scrollLeft += drifted;
	};

	const speed = props.speed;
	useEffect(() => {
		if (reduce) return;
		const el = scroller.current;
		const inner = track.current;
		if (!el || !inner) return;

		const start = () => {
			// Cancelling alone would snap the row back to where the drift started.
			fold();
			// Snap areas move with the transform, so the scroller re-snaps after them and the row stands still.
			el.style.scrollSnapType = 'none';
			// Layout width, which the transform does not change; `scrollWidth` would shrink as the drift advanced.
			const full = inner.offsetWidth - el.clientWidth;
			if (full <= 0 || held.current) return;
			// The whole row, including back over what was scrolled, or a reader at the last tile leaves nothing to animate.
			const scrolled = el.scrollLeft;
			drift.current = inner.animate(
				{ transform: [`translate3d(${scrolled}px,0,0)`, `translate3d(${scrolled - full}px,0,0)`] },
				{ duration: full / speed, direction: 'alternate', iterations: Infinity, easing: 'linear' },
			);
			// Resume from the reader's position rather than the first tile.
			drift.current.currentTime = scrolled / speed;
			if (!onScreen.current) drift.current.pause();
		};
		restart.current = start;
		start();

		const visibility = new IntersectionObserver(([entry]) => {
			onScreen.current = entry?.isIntersecting ?? false;
			if (onScreen.current) drift.current?.play();
			else drift.current?.pause();
		});
		visibility.observe(el);

		// The distance depends on both widths, so a rotation or font swap rebuilds the animation.
		const resize = new ResizeObserver(start);
		resize.observe(el);
		resize.observe(inner);

		return () => {
			visibility.disconnect();
			resize.disconnect();
			drift.current?.cancel();
			drift.current = null;
			restart.current = null;
		};
	}, [reduce, speed]);

	/** Every scroll defers the restart, so the drift never overtakes a fling still travelling. */
	const defer = () => {
		if (resumeTimer.current) clearTimeout(resumeTimer.current);
		resumeTimer.current = setTimeout(() => restart.current?.(), DRIFT_RESUME_MS);
	};

	const takeOver = () => {
		if (resumeTimer.current) clearTimeout(resumeTimer.current);
		fold();
		if (scroller.current) scroller.current.style.scrollSnapType = '';
	};

	const hold = () => {
		held.current = true;
		takeOver();
	};

	// On the window: a swipe ending off the row releases there, and a missed release never drifts again. Not in
	// `onScroll`, which the fold's own write fires a frame later, under a finger still resting on the row.
	useEffect(() => {
		const release = () => {
			if (!held.current) return;
			held.current = false;
			defer();
		};
		window.addEventListener('pointerup', release);
		window.addEventListener('pointercancel', release);
		return () => {
			window.removeEventListener('pointerup', release);
			window.removeEventListener('pointercancel', release);
			if (resumeTimer.current) clearTimeout(resumeTimer.current);
		};
	}, []);

	return (
		<div
			ref={scroller}
			// A wheel sends no pointer event, so it folds the drift itself and has no release to wait for.
			onWheel={() => {
				takeOver();
				defer();
			}}
			onPointerDown={hold}
			onScroll={defer}
			className="hide-scrollbar snap-x [scroll-padding-left:1.5rem] overflow-x-auto"
		>
			{/* `w-max`, or the track is scroller-width and the drift reads no real distance. */}
			<div ref={track} className="flex w-max gap-4 px-6">
				{props.children}
			</div>
		</div>
	);
}
